import type {
  CreateMapNoteEntryInput,
  MapNoteEntry,
  NoteAudience,
  UpdateMapNoteEntryInput,
} from "@west-marches/shared";
import { apiFetch } from "@/lib/apiClient";

async function readJson<T>(response: Response): Promise<T> {
  const payload = (await response.json().catch(() => ({}))) as T & {
    error?: string;
  };

  if (!response.ok) {
    const message =
      typeof payload.error === "string" && payload.error ? payload.error : "Request failed";
    throw new Error(message);
  }

  return payload;
}

function entriesBasePath(campaignId: string, mapNoteId: string): string {
  return `/api/campaigns/${encodeURIComponent(campaignId)}/map-notes/${encodeURIComponent(mapNoteId)}/entries`;
}

export async function getRootEntry(
  campaignId: string,
  mapNoteId: string,
  audience: NoteAudience = "gm",
  options: { asPlayer?: boolean } = {},
): Promise<MapNoteEntry> {
  const params = new URLSearchParams({ audience });
  if (options.asPlayer) {
    params.set("asPlayer", "1");
  }
  const url = `${entriesBasePath(campaignId, mapNoteId)}?${params.toString()}`;
  const response = await apiFetch(url, { method: "GET" });
  const payload = await readJson<{ entry: MapNoteEntry }>(response);
  return payload.entry;
}

export async function getEntry(
  campaignId: string,
  mapNoteId: string,
  entryId: string,
): Promise<MapNoteEntry> {
  const response = await apiFetch(
    `${entriesBasePath(campaignId, mapNoteId)}/${encodeURIComponent(entryId)}`,
    { method: "GET" },
  );
  const payload = await readJson<{ entry: MapNoteEntry }>(response);
  return payload.entry;
}

export async function listChildEntries(
  campaignId: string,
  mapNoteId: string,
  parentEntryId: string,
): Promise<MapNoteEntry[]> {
  const response = await apiFetch(
    `${entriesBasePath(campaignId, mapNoteId)}/${encodeURIComponent(parentEntryId)}/children`,
    { method: "GET" },
  );
  const payload = await readJson<{ entries: MapNoteEntry[] }>(response);
  return payload.entries;
}

export async function createChildEntry(
  campaignId: string,
  mapNoteId: string,
  parentEntryId: string,
  input: CreateMapNoteEntryInput,
): Promise<MapNoteEntry> {
  const response = await apiFetch(
    `${entriesBasePath(campaignId, mapNoteId)}/${encodeURIComponent(parentEntryId)}/children`,
    { method: "POST", body: JSON.stringify(input) },
  );
  const payload = await readJson<{ entry: MapNoteEntry }>(response);
  return payload.entry;
}

export async function updateEntry(
  campaignId: string,
  mapNoteId: string,
  entryId: string,
  input: UpdateMapNoteEntryInput,
): Promise<MapNoteEntry> {
  const response = await apiFetch(
    `${entriesBasePath(campaignId, mapNoteId)}/${encodeURIComponent(entryId)}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
  const payload = await readJson<{ entry: MapNoteEntry }>(response);
  return payload.entry;
}

export async function deleteEntry(
  campaignId: string,
  mapNoteId: string,
  entryId: string,
): Promise<void> {
  const response = await apiFetch(
    `${entriesBasePath(campaignId, mapNoteId)}/${encodeURIComponent(entryId)}`,
    { method: "DELETE" },
  );
  await readJson<{ ok: boolean }>(response);
}
